import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useParams } from "react-router-dom";
import { getBrokerVersions } from "../../../Services/BrokerTransactionsApi";
import { getPlane } from "../../../Services/plansApi"; // جلب أسماء الباقات
import BrokerTransactionTap from "./BrokerTransactionTap"; // التاب الجديد لعرض كشف الحساب

const BrokerTransactionPage = ({ brokerId: propBrokerId, initialResponse = null, initialUserId = null, brokerInfo = null }) => {
  const params = useParams?.() || {};
  const brokerId = propBrokerId ?? params.brokerId ?? params.id ?? null;

  // تاب أعلى الصفحة: 0 = الإصدارات (الحالي)، 1 = كشف حساب
  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = (e, v) => setTabIndex(v);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]); // each item: { broker_version, tenant, client, domains, ... }
  const [search, setSearch] = useState("");
  const [plansMap, setPlansMap] = useState({}); // id -> plane_name map

  // جلب قائمة الباقات مرة واحدة وبناء خريطة id -> name
  useEffect(() => {
    let mounted = true;
    const fetchPlans = async () => {
      try {
        const res = await getPlane();
        const plansArray = res?.data?.data?.palnes ?? res?.data?.palnes ?? [];
        const map = {};
        if (Array.isArray(plansArray)) {
          plansArray.forEach((p) => {
            if (p && p.id !== undefined) map[String(p.id)] = p.plane_name ?? p.plane_name_en ?? p.name ?? p.title ?? "";
          });
        }
        if (mounted) setPlansMap(map);
      } catch (err) {
        console.warn("getPlane error:", err);
      }
    };
    fetchPlans();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!brokerId && !initialUserId && !initialResponse) return;
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      setError("");
      try {
        // نحاول الحصول على user_id أولاً (يمكن أن يمرَّ كـ initialUserId أو داخل initialResponse)
        const uid = initialUserId ?? initialResponse?.data?.[0]?.user_id ?? initialResponse?.user_id ?? null;

        // إذا وُجد uid نمرّره كوسيط واحد إلى getBrokerVersions (user_id فقط)
        if (uid) {
          const res = await getBrokerVersions(uid);
          const data = res?.data?.data ?? res?.data ?? [];
          if (mounted) setItems(Array.isArray(data) ? data : []);
          return;
        }

        // إذا وُجدت initialResponse كمصفوفة استخدمها مباشرة
        if (initialResponse && Array.isArray(initialResponse?.data) && initialResponse.data.length > 0) {
          if (mounted) setItems(initialResponse.data);
          return;
        }

        // الحالة الافتراضية: جلب الإصدارات باستخدام brokerId في المسار
        const res = await getBrokerVersions(brokerId);
        const data = res?.data?.data ?? res?.data ?? [];
        if (mounted) setItems(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("getBrokerVersions error:", err);
        setError(err?.response?.data?.message || err.message || "فشل جلب بيانات الموزع");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, [brokerId, initialResponse, initialUserId]);

  const fmtDate = (d) => {
    if (!d) return "-";
    try {
      return new Date(d).toLocaleString("ar-EG");
    } catch { return d; }
  };

  const matchesSearch = (row, q) => {
    if (!q) return true;
    const t = q.toLowerCase();
    const vname = String(row?.broker_version?.version_name ?? "").toLowerCase();
    const tenantId = String(row?.tenant?.id ?? "").toLowerCase();
    const clientName = String(row?.client?.name ?? "").toLowerCase();
    return vname.includes(t) || tenantId.includes(t) || clientName.includes(t);
  };

  const visible = items.filter((r) => matchesSearch(r, search));
  const getPlanName = (id) => {
    if (!id) return "-";
    return plansMap[String(id)] ?? String(id);
  };

  return (
    <Box dir="rtl" sx={{ p: 3 }}>
      {/* Tabs أعلى الصفحة */}
      <Paper sx={{ mb: 2, p: 1 }}>
        <Tabs value={tabIndex} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
          <Tab label="الإصدارات" />
          <Tab label="كشف حساب" />
        </Tabs>
      </Paper>

      {/* عرض التاب المختار */}
      {tabIndex === 1 ? (
        /* تاب: كشف حساب - نمرّر payload الذي يحتوي message + data[{ user_id }] عبر initialUserPayload */
        <BrokerTransactionTap
          brokerId={brokerId}
          initialUserId={initialUserId ?? brokerInfo?.user?.id ?? brokerInfo?.user_id ?? null}
          initialBrokerData={brokerInfo ?? (initialResponse?.data?.[0] ?? null)}
          initialUserPayload={
            initialResponse ??
            {
              message: "✅ تم تحديث بيانات المستخدم بنجاح",
              data: [{ user_id: brokerInfo?.user?.id ?? brokerInfo?.user_id ?? null }],
            }
          }
        />
      ) : (
        /* تاب: الإصدارات - المحتوى الحالي للمكوّن كما كان */
        <>
          {/* عرض بيانات الموزع الممرّرة من الصفحة الرئيسية إذا وُجدت */}
          {brokerInfo && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{brokerInfo.name ?? brokerInfo.user?.name ?? "-"}</Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>{brokerInfo.user?.email ?? brokerInfo.email ?? "-"}</Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="body2">الهاتف: {brokerInfo.phone1 ?? brokerInfo.user?.phone ?? "-"}</Typography>
                  <Typography variant="body2">كود الموزع: {brokerInfo.user?.broker_code ?? brokerInfo.broker_code ?? "-"}</Typography>
                  <Typography variant="body2">المدينة: {brokerInfo.city ?? "-"}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2">الحالة: {brokerInfo.user?.active ? "مفعل" : "معطل"}</Typography>
                </Box>
              </Box>
            </Paper>
          )}

          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="ابحث بالنسخة، معرف المستأجر أو اسم العميل..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch("")}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Box>
          </Paper>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Paper sx={{ p: 2, background: "#ffecec" }}>
              <Typography color="error">{error}</Typography>
            </Paper>
          ) : (
            <Paper sx={{ p: 1 }}>
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell align="right">العميل</TableCell>
                      <TableCell align="right">اسم الإصدار</TableCell>
                      <TableCell align="right">التكلفة</TableCell>
                      <TableCell align="right">الباقة</TableCell>
                      <TableCell align="right">بداية</TableCell>
                      <TableCell align="right">نهاية</TableCell>
                      <TableCell align="right">الدومينات</TableCell>
                      <TableCell align="right">أنشئت</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center">لا توجد بيانات</TableCell>
                      </TableRow>
                    ) : (
                      visible.map((row, idx) => {
                        const bv = row.broker_version ?? {};
                        const tenant = row.tenant ?? {};
                        const client = row.client ?? {};
                        const domains = (row.domains ?? tenant.domains ?? []).map(d => d.domain || d).filter(Boolean);
                        return (
                          <TableRow key={bv.id ?? `${idx}`}>
                            <TableCell align="right">
                              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.3 }}>
                                <Typography sx={{ fontWeight: 700 }}>
                                  {client.name ?? "-"} <Typography component="span" variant="caption" sx={{ color: "text.secondary", ml: 1 }}>#{client.id ?? "-"}</Typography>
                                </Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>{client.email ?? "-"}</Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>{client.phone ?? "-"}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">{bv.version_name ?? "-"}</TableCell>
                            <TableCell align="right">{bv.cost ?? "-"}</TableCell>
                            <TableCell align="right">{getPlanName(tenant.plane_id ?? row.broker_version?.plane_id)}</TableCell>
                            <TableCell align="right">{tenant.start_date ?? "-"}</TableCell>
                            <TableCell align="right">{tenant.end_date ?? "-"}</TableCell>
                            <TableCell align="right" sx={{ maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={domains.join(", ")}>
                              {domains.join(", ") || "-"}
                            </TableCell>
                            <TableCell align="right">{fmtDate(bv.created_at ?? tenant.created_at)}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
          {/* نهاية محتوى تبويب الإصدارات */}
        </>
      )}
    </Box>
  );
};

export default BrokerTransactionPage;